import type { MarketingOptOutCoverage } from '~/types/person/marketing-optouts';
import { pbRequest, pbRequestVoid } from '~/services/request';

export interface PersonOption {
    person: boolean;
    license: boolean;
    enabled: boolean;
}

export interface PersonOptions {
    data_brokers: {
        db_people_search: PersonOption;
        db_people_search_affiliate: PersonOption;
        db_list: PersonOption;
        db_job_recruiting: PersonOption;
        db_advertising_marketing: PersonOption;
        db_background_check: PersonOption;
        db_data_enrichment: PersonOption;
        db_healthcare_insurance: PersonOption;
        db_geolocation: PersonOption;
        db_surveillance_tech: PersonOption;
        db_work_verification: PersonOption;
    };
    monitoring: {
        search_monitoring: PersonOption;
        social_media: PersonOption;
        dark_web: PersonOption;
        paste_site: PersonOption;
    };
    privacy: {
        marketing_suppression: PersonOption;
        blur_location: PersonOption;
        share_email: PersonOption;
    };
    license: {
        has_active_license: boolean;
        data_brokers_setting: number | null;
    };
    // Null whenever the marketing opt-out store could not be read — never render as zero coverage.
    coverage: MarketingOptOutCoverage | null;
    /** Preconditions a control needs before it can be turned on. Derived, never written back. */
    requirements: {
        blur_location: { proof_of_address: boolean };
    };
}

export type PersonOptionGroups = Omit<PersonOptions, 'license' | 'coverage' | 'requirements'>;
export type PersonOptionSection = keyof PersonOptionGroups;
export type PersonOptionKey<Section extends PersonOptionSection> = Section extends PersonOptionSection
    ? keyof PersonOptionGroups[Section] & string
    : never;
/**
 * Only what a write changes. The endpoint reads its stored row and defaults every key the payload
 * omits to the value it already holds, so an option left out is an option left alone — while an
 * option sent back unchanged is this client asserting a value it may have read some time ago,
 * over whatever another writer has done since.
 */
export type PersonOptionsUpdatePayload = {
    [Section in PersonOptionSection]?: Partial<PersonOptionGroups[Section]>;
};

export const personOptionsService = {
    async getOptions(personId: number): Promise<PersonOptions> {
        const nuxtApp = useNuxtApp();

        return await pbRequest<PersonOptions>(nuxtApp.$api.routes.people.options.get(personId), {
            failure: 'Failed to fetch options',
        });
    },

    async updateOptions(personId: number, options: PersonOptionsUpdatePayload): Promise<void> {
        const nuxtApp = useNuxtApp();

        await pbRequestVoid(nuxtApp.$api.routes.people.options.update(personId), {
            method: 'PATCH',
            body: options,
            expect: 201,
            failure: 'Failed to update options',
        });
    },
};
